import type { ButtonVariantProps } from './Button.variants';

/**
 * Icon sizing per Button size. Keeps icons proportional to the button's
 * height. Values come from the Figma per-size bands of node 294:1855.
 */
export const ICON_SIZE_FOR: Record<NonNullable<ButtonVariantProps['size']>, number> = {
  xxs: 14,
  xs: 14,
  s: 16,
  m: 16,
  l: 18,
  xl: 20,
  xxl: 22,
};

/**
 * Resolved icon color per (variant, dark-mode). Mirrors `buttonTextVariants`.
 * We resolve to a hex string here because `lucide-react-native` and
 * `react-native-svg` accept `color` as a prop, not a className.
 *
 * Disabled state is NOT a separate color path: per Figma node 301:2803,
 * disabled buttons render with the default palette + `opacity-30` applied
 * to the whole button — which the icon inherits via its parent's opacity.
 */
type Variant = NonNullable<ButtonVariantProps['variant']>;

const ICON_COLOR: Record<Variant, { light: string; dark: string }> = {
  // text-primary-50 (both modes)
  primary: { light: 'rgb(245, 243, 255)', dark: 'rgb(245, 243, 255)' },
  // text-secondary-600 (#525252) /  (#a1a1a1)
  secondary: { light: 'rgb(82, 82, 82)', dark: 'rgb(161, 161, 161)' },
  // Same as secondary — both use the muted gray text per Figma 301:2509/301:2500.
  tertiary: { light: 'rgb(82, 82, 82)', dark: 'rgb(161, 161, 161)' },
  // Ghost variant — same icon palette as tertiary per Button.variants.ts.
  ghost: { light: 'rgb(82, 82, 82)', dark: 'rgb(161, 161, 161)' },
  // text-green-50 (both modes)
  success: { light: 'rgb(240, 253, 244)', dark: 'rgb(240, 253, 244)' },
  // text-red-50 (both modes)
  alert: { light: 'rgb(254, 242, 242)', dark: 'rgb(254, 242, 242)' },
};

export function resolveButtonIconColor(
  variant: Variant,
  // Kept in the signature for API stability; ignored because disabled is
  // now an opacity treatment (icon inherits opacity from the Button).
  _disabled: boolean,
  isDark: boolean,
): string {
  const pair = ICON_COLOR[variant];
  return isDark ? pair.dark : pair.light;
}
